import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { signOut } from "firebase/auth";
import { Camera, Check, ChevronRight, Compass, CreditCard, ExternalLink, Headphones, LoaderCircle, LogOut, Mail, MoonStar, Pencil, Play, RotateCcw, Timer, Trash2, User, Wind, X } from "lucide-react";
import { db, functions, auth } from "../firebase-config.js";
import { tooltipManager } from "../utils/tooltipManager";
import TopbarInner from "../components/topbar/TopbarInner";
import NatureFavoritesEditor from "../components/ai/NatureFavoritesEditor.jsx";
import { loadPreferences, removeFavorite, unhideAllSessions } from "../services/ai/memory/aiMemory.js";

const PRIMARY_NEEDS = Object.freeze([
  {
    key: "relax",
    Icon: Wind,
    label: "Me détendre",
    description: "Relâcher la pression et retrouver du calme.",
    summary: "Un refuge plus doux pour laisser la tension redescendre.",
  },
  {
    key: "rest",
    Icon: MoonStar,
    label: "Me reposer",
    description: "Ralentir et laisser davantage de place au repos.",
    summary: "Un moment enveloppant pour ralentir naturellement.",
  },
  {
    key: "recenter",
    Icon: Compass,
    label: "Me recentrer",
    description: "Retrouver un point d’ancrage et plus de clarté.",
    summary: "Une pause pour revenir doucement à l’essentiel.",
  },
]);

const DAILY_TIMES = Object.freeze([
  {
    value: "30sec",
    label: "30 secondes",
    description: "Un souffle pour commencer.",
  },
  {
    value: "2min",
    label: "2 minutes",
    description: "Une vraie pause dans la journée.",
  },
  {
    value: "5min",
    label: "5 minutes",
    description: "Un rituel plus profond.",
  },
  {
    value: "10min",
    label: "10 minutes",
    description: "Un temps durable pour vous.",
  },
]);

const PRIMARY_NEED_KEYS = new Set(
  PRIMARY_NEEDS.map(
    (option) => option.key,
  ),
);

function normalizePrimaryNeed(
  profile = {},
) {
  return PRIMARY_NEED_KEYS.has(
    profile.primaryNeed,
  )
    ? profile.primaryNeed
    : "relax";
}

function getInitial(user) {
  const source = user?.displayName || user?.email || "?";
  return source.trim().charAt(0).toUpperCase() || "?";
}

const PROFILE_PHOTO_MAX_BYTES = 8 * 1024 * 1024;
const PROFILE_PHOTO_SIZE = 384;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Impossible de lire cette image."));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Cette image ne peut pas être utilisée."));
    image.src = src;
  });
}

async function createProfilePhoto(file) {
  if (!file?.type?.startsWith("image/")) {
    throw new Error("Choisissez une image au format JPG, PNG ou WebP.");
  }

  if (file.size > PROFILE_PHOTO_MAX_BYTES) {
    throw new Error("Cette image est trop lourde. La taille maximale est de 8 Mo.");
  }

  const source = await readFileAsDataUrl(file);
  const image = await loadImage(source);
  const canvas = document.createElement("canvas");
  canvas.width = PROFILE_PHOTO_SIZE;
  canvas.height = PROFILE_PHOTO_SIZE;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Impossible de préparer cette image.");
  }

  const sourceSize = Math.min(image.naturalWidth, image.naturalHeight);
  const sourceX = (image.naturalWidth - sourceSize) / 2;
  const sourceY = (image.naturalHeight - sourceSize) / 2;

  context.fillStyle = "#f4f7f5";
  context.fillRect(0, 0, PROFILE_PHOTO_SIZE, PROFILE_PHOTO_SIZE);
  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceSize,
    sourceSize,
    0,
    0,
    PROFILE_PHOTO_SIZE,
    PROFILE_PHOTO_SIZE,
  );

  return canvas.toDataURL("image/jpeg", 0.84);
}

function formatMemberSince(metadata) {
  if (!metadata?.creationTime) return null;

  try {
    return new Date(metadata.creationTime).toLocaleDateString("fr-FR", {
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function getPlanLabel(isPremium, billingInfo) {
  if (billingInfo?.subscriptionStatus === "trialing") {
    return "Essai en cours";
  }

  return isPremium ? "Refuge Personnel" : "Découverte";
}

function PreferenceChoice({ option, selected, onClick }) {
  const { Icon } = option;

  return (
    <button
      type="button"
      className={`profile-choice ${selected ? "is-selected" : ""}`}
      aria-pressed={selected}
      onClick={onClick}
    >
      <span className="profile-choice__icon" aria-hidden="true">
        <Icon size={21} strokeWidth={1.9} />
      </span>

      <span className="profile-choice__copy">
        <strong>{option.label}</strong>
        <span>{option.description}</span>
      </span>

      <span className="profile-choice__check" aria-hidden="true">
        {selected ? <Check size={17} strokeWidth={2.4} /> : null}
      </span>
    </button>
  );
}

function SettingsRow({
  Icon,
  title,
  description,
  actionLabel,
  onClick,
  danger = false,
  disabled = false,
}) {
  return (
    <button
      type="button"
      className={`profile-settings-row ${danger ? "is-danger" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="profile-settings-row__icon" aria-hidden="true">
        <Icon size={20} strokeWidth={1.9} />
      </span>

      <span className="profile-settings-row__copy">
        <strong>{title}</strong>
        {description ? <span>{description}</span> : null}
      </span>

      <span className="profile-settings-row__action">
        {actionLabel ? <span>{actionLabel}</span> : null}
        <ChevronRight size={18} strokeWidth={2} aria-hidden="true" />
      </span>
    </button>
  );
}

function formatRefugeDate(value) {
  const millis = Number(value);
  if (!Number.isFinite(millis)) return "";

  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
    }).format(new Date(millis));
  } catch {
    return "";
  }
}

function describeRefuge(favorite) {
  const parts = [];

  if (favorite?.breathing?.name) parts.push(favorite.breathing.name);

  const ambiances = (favorite?.ambiances || [])
    .map((item) => item?.name)
    .filter(Boolean);

  if (ambiances.length) parts.push(ambiances.join(" · "));

  return parts.join(" · ");
}

function AuriaRefugesPanel({ uid, onSuccess }) {
  const navigate = useNavigate();

  const [favorites, setFavorites] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [isLoadingRefuges, setIsLoadingRefuges] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [refugeError, setRefugeError] = useState("");

  useEffect(() => {
    if (!uid) return undefined;

    let cancelled = false;
    setIsLoadingRefuges(true);

    loadPreferences(uid)
      .then((preferences) => {
        if (cancelled) return;
        setFavorites(preferences.favorites || []);
        setHiddenCount((preferences.hiddenSessions || []).length);
      })
      .catch((loadError) => {
        console.warn("Refuges enregistrés indisponibles :", loadError);
        if (!cancelled) setRefugeError("Vos refuges n’ont pas pu être chargés.");
      })
      .finally(() => {
        if (!cancelled) setIsLoadingRefuges(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uid]);

  const replayRefuge = (favorite) => {
    if (!favorite?.arc?.tracks?.length) return;

    navigate("/ia/player", {
      state: {
        session: {
          ...favorite,
          id: `auria-replay-${Date.now()}`,
          context: {
            ...(favorite.context || {}),
            replayedFromFavorite: true,
          },
        },
        stateBefore: favorite.context?.stateBefore ?? 5,
        intent: favorite.context?.intent || "relax",
        withBreathing: favorite.context?.withBreathing === true,
      },
    });
  };

  const forgetRefuge = async (favoriteId) => {
    if (!uid || busyId) return;

    setBusyId(favoriteId);
    setRefugeError("");

    try {
      const next = await removeFavorite(uid, favoriteId);
      setFavorites(next?.favorites || []);
    } catch (removeError) {
      console.warn("Retrait du refuge impossible :", removeError);
      setRefugeError("Ce refuge n’a pas pu être retiré. Vous pouvez réessayer.");
    } finally {
      setBusyId(null);
    }
  };

  const restoreHiddenSessions = async () => {
    if (!uid || isRestoring) return;

    setIsRestoring(true);
    setRefugeError("");

    try {
      await unhideAllSessions(uid);
      setHiddenCount(0);
      onSuccess?.("Toutes vos compositions peuvent à nouveau vous être proposées.");
    } catch (restoreError) {
      console.warn("Réactivation impossible :", restoreError);
      setRefugeError("Les compositions n’ont pas pu être réactivées. Vous pouvez réessayer.");
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <section className="profile-panel profile-panel--refuges">
      <div className="profile-panel__head">
        <div>
          <p className="profile-eyebrow">Refuge personnel</p>

          <h2>Mes refuges enregistrés</h2>

          <p>
            Retrouvez les compositions que vous avez aimées et réécoutez-les
            quand vous le souhaitez.
          </p>
        </div>
      </div>

      {isLoadingRefuges ? (
        <p className="profile-refuges__empty">Chargement…</p>
      ) : favorites.length > 0 ? (
        <ul className="profile-refuges__list">
          {favorites.map((favorite) => (
            <li key={favorite.favoriteId} className="profile-refuges__item">
              <div className="profile-refuges__copy">
                <strong>{favorite.title || "Votre refuge"}</strong>

                {describeRefuge(favorite) ? (
                  <span className="profile-refuges__detail">{describeRefuge(favorite)}</span>
                ) : null}

                {formatRefugeDate(favorite.savedAt) ? (
                  <span className="profile-refuges__date">Enregistré le {formatRefugeDate(favorite.savedAt)}</span>
                ) : null}
              </div>

              <div className="profile-refuges__actions">
                <button
                  type="button"
                  className="profile-refuges__play"
                  onClick={() => replayRefuge(favorite)}
                  aria-label={`Réécouter ${favorite.title || "ce refuge"}`}
                >
                  <Play size={15} strokeWidth={2.4} aria-hidden="true" />
                  Réécouter
                </button>

                <button
                  type="button"
                  className="profile-refuges__forget"
                  onClick={() => forgetRefuge(favorite.favoriteId)}
                  disabled={busyId === favorite.favoriteId}
                  aria-label={`Retirer ${favorite.title || "ce refuge"} de vos refuges`}
                >
                  <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="profile-refuges__empty">Les compositions que vous aimez apparaîtront ici.</p>
      )}

      {hiddenCount > 0 ? (
        <div className="profile-refuges__hidden">
          <p>{hiddenCount} composition{hiddenCount > 1 ? "s" : ""} ne vous {hiddenCount > 1 ? "sont" : "est"} plus proposée{hiddenCount > 1 ? "s" : ""}.</p>

          <button
            type="button"
            className="profile-refuges__restore"
            onClick={restoreHiddenSessions}
            disabled={isRestoring}
          >
            <RotateCcw size={15} strokeWidth={2.2} aria-hidden="true" />
            {isRestoring ? "Réactivation…" : "Tout réactiver"}
          </button>
        </div>
      ) : null}

      {refugeError ? (
        <div className="profile-notice is-error profile-refuges__error" role="alert">
          <X size={18} strokeWidth={2.2} aria-hidden="true" />
          {refugeError}
        </div>
      ) : null}
    </section>
  );
}

export default function ProfilePage({
  user,
  isPremium,
  longestStreak = 0,
  onLogout,
  onThemeToggle,
  onboardingProfile,
  onUpdateOnboarding,
  naturalSounds = [],
  embedded = false,
}) {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const photoMenuRef = useRef(null);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [localPrimaryNeed, setLocalPrimaryNeed] = useState("relax");
  const [localDailyTime, setLocalDailyTime] = useState("2min");
  const [showEmailChooser, setShowEmailChooser] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [billingInfo, setBillingInfo] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(() => {
    if (typeof window === "undefined" || !user?.uid) return user?.photoURL || "";

    try {
      const photoWasRemoved =
        localStorage.getItem(`profilePhotoRemoved:${user.uid}`) === "1";

      if (photoWasRemoved) return "";

      return localStorage.getItem(`profilePhoto:${user.uid}`) || user?.photoURL || "";
    } catch {
      return user?.photoURL || "";
    }
  });
  const [isPhotoSaving, setIsPhotoSaving] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [favoritesEditorOpen, setFavoritesEditorOpen] = useState(false);

  const supportEmail = "harmonauria.music@gmail.com";
  const supportSubject = "Besoin d’aide / Informations";
  const supportBody = `Bonjour !\n\n${
    user?.email ? `Mon email de compte : ${user.email}\n\n` : ""
  }${supportMessage.trim()}\n\nMerci.`;

  const mailtoHref = `mailto:${supportEmail}?subject=${encodeURIComponent(
    supportSubject,
  )}&body=${encodeURIComponent(supportBody)}`;
  const gmailHref = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
    supportEmail,
  )}&su=${encodeURIComponent(supportSubject)}&body=${encodeURIComponent(
    supportBody,
  )}`;
  const outlookHref = `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(
    supportEmail,
  )}&subject=${encodeURIComponent(supportSubject)}&body=${encodeURIComponent(
    supportBody,
  )}`;
  const yahooHref = `https://compose.mail.yahoo.com/?to=${encodeURIComponent(
    supportEmail,
  )}&subject=${encodeURIComponent(supportSubject)}&body=${encodeURIComponent(
    supportBody,
  )}`;

  const selectedNeed = useMemo(
    () =>
      PRIMARY_NEEDS.find(
        (option) => option.key === normalizePrimaryNeed(onboardingProfile),
      ) || PRIMARY_NEEDS[0],
    [onboardingProfile],
  );

  const selectedDailyTime = useMemo(
    () =>
      DAILY_TIMES.find(
        (option) => option.value === onboardingProfile?.dailyTime,
      ) || DAILY_TIMES[1],
    [onboardingProfile?.dailyTime],
  );

  useEffect(() => {
    setLocalPrimaryNeed(normalizePrimaryNeed(onboardingProfile));
    setLocalDailyTime(onboardingProfile?.dailyTime || "2min");
  }, [onboardingProfile]);

  useEffect(() => {
    if (!user?.uid) {
      setBillingInfo(null);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      doc(db, "users", user.uid),
      (snapshot) => {
const data = snapshot.exists()
  ? snapshot.data()
  : {};

setBillingInfo({
          premiumMode: data.premiumMode || null,
          subscriptionId: data.subscriptionId || null,
          subscriptionStatus: data.subscriptionStatus || null,
          stripeCustomerId: data.stripeCustomerId || null,
          cancelAt: data.cancelAt || null,
          currentPeriodEnd: data.currentPeriodEnd || null,
          cancelAtPeriodEnd: Boolean(data.cancelAtPeriodEnd),
        });

        const hasProfilePhotoField = Object.prototype.hasOwnProperty.call(
          data,
          "profilePhoto",
        );

        const savedPhoto = hasProfilePhotoField
          ? typeof data.profilePhoto === "string" &&
            data.profilePhoto.startsWith("data:image/")
            ? data.profilePhoto
            : ""
          : user.photoURL || "";

        setProfilePhoto(savedPhoto);

        try {
          if (savedPhoto) {
            localStorage.setItem(`profilePhoto:${user.uid}`, savedPhoto);
            localStorage.removeItem(`profilePhotoRemoved:${user.uid}`);
          } else if (hasProfilePhotoField) {
            localStorage.removeItem(`profilePhoto:${user.uid}`);
            localStorage.setItem(`profilePhotoRemoved:${user.uid}`, "1");
          }
        } catch {
          // Le cache local est facultatif : Firestore reste la source principale.
        }
      },
      (loadError) => {
        console.warn("Impossible de charger les informations de facturation :", loadError);
        setBillingInfo(null);
      },
    );

    return () => unsubscribe();
}, [user?.uid, user?.photoURL]);

  useEffect(() => {
    if (!user?.uid) {
      setProfilePhoto("");
      return;
    }

    try {
      const photoWasRemoved =
        localStorage.getItem(`profilePhotoRemoved:${user.uid}`) === "1";
      const cachedPhoto = localStorage.getItem(`profilePhoto:${user.uid}`);

      setProfilePhoto(photoWasRemoved ? "" : cachedPhoto || user.photoURL || "");
    } catch {
      setProfilePhoto(user.photoURL || "");
    }
  }, [user?.uid, user?.photoURL]);

  useEffect(() => {
    if (!showPhotoMenu) return undefined;

    const handlePointerDown = (event) => {
      if (!photoMenuRef.current?.contains(event.target)) {
        setShowPhotoMenu(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowPhotoMenu(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showPhotoMenu]);

  useEffect(() => {
    if (!showEmailChooser) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousTouchAction = body.style.touchAction;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.touchAction = "none";

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.touchAction = previousTouchAction;
    };
  }, [showEmailChooser]);

  useEffect(() => {
    if (!successMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage("");
    }, 3200);

    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);

  const handleProfilePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    setShowPhotoMenu(false);

    if (!file || !user?.uid || isPhotoSaving) return;

    setPhotoError("");
    setIsPhotoSaving(true);

    try {
      const preparedPhoto = await createProfilePhoto(file);

      await setDoc(
        doc(db, "users", user.uid),
        {
          profilePhoto: preparedPhoto,
          profilePhotoUpdatedAt: new Date().toISOString(),
        },
        { merge: true },
      );

      setProfilePhoto(preparedPhoto);

      try {
        localStorage.setItem(`profilePhoto:${user.uid}`, preparedPhoto);
        localStorage.removeItem(`profilePhotoRemoved:${user.uid}`);
      } catch {
        // La photo reste synchronisée dans Firestore.
      }

      setSuccessMessage("Photo de profil mise à jour.");
    } catch (photoSaveError) {
      console.error("Impossible de mettre à jour la photo de profil :", photoSaveError);
      setPhotoError(
        photoSaveError?.message || "Impossible de mettre à jour cette photo.",
      );
    } finally {
      setIsPhotoSaving(false);
    }
  };

  const handleProfilePhotoDelete = async () => {
    if (!user?.uid || !profilePhoto || isPhotoSaving) return;

    const confirmed = window.confirm("Supprimer votre photo de profil ?");
    if (!confirmed) return;

    setShowPhotoMenu(false);
    setPhotoError("");
    setIsPhotoSaving(true);

    try {
      await setDoc(
        doc(db, "users", user.uid),
        {
          profilePhoto: "",
          profilePhotoUpdatedAt: new Date().toISOString(),
        },
        { merge: true },
      );

      setProfilePhoto("");

      try {
        localStorage.removeItem(`profilePhoto:${user.uid}`);
        localStorage.setItem(`profilePhotoRemoved:${user.uid}`, "1");
      } catch {
        // La suppression reste synchronisée dans Firestore.
      }

      setSuccessMessage("Photo de profil supprimée.");
    } catch (photoDeleteError) {
      console.error(
        "Impossible de supprimer la photo de profil :",
        photoDeleteError,
      );
      setPhotoError("Impossible de supprimer la photo de profil.");
    } finally {
      setIsPhotoSaving(false);
    }
  };

  if (!user) {
    return (
      <main className="profile-empty-state">
        <h1>Mon refuge</h1>
        <p>Connectez-vous pour accéder à votre profil.</p>
      </main>
    );
  }

  function openWithFallback(appHref, webHref) {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (!isMobile) {
      window.open(webHref, "_blank", "noreferrer");
      return;
    }

    let fallbackTimer = window.setTimeout(() => {
      if (document.visibilityState === "visible") {
        window.open(webHref, "_blank", "noreferrer");
      }
    }, 700);

    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState === "hidden") {
          window.clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
      },
      { once: true },
    );

    window.location.href = appHref;
  }

  const openGmailCompose = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);

    if (!isIOS && !isAndroid) {
      window.open(gmailHref, "_blank", "noreferrer");
      return;
    }

    const encodedTo = encodeURIComponent(supportEmail);
    const encodedSubject = encodeURIComponent(supportSubject);
    const encodedBody = encodeURIComponent(supportBody);
    const gmailAppHrefIOS = `googlegmail:///co?to=${encodedTo}&subject=${encodedSubject}&body=${encodedBody}`;
    const gmailAppHrefAndroid = `intent://co?to=${encodedTo}&subject=${encodedSubject}&body=${encodedBody}#Intent;scheme=googlegmail;package=com.google.android.gm;end`;

    openWithFallback(
      isIOS ? gmailAppHrefIOS : gmailAppHrefAndroid,
      gmailHref,
    );
  };

  const openOutlookCompose = () => {
    const encodedTo = encodeURIComponent(supportEmail);
    const encodedSubject = encodeURIComponent(supportSubject);
    const encodedBody = encodeURIComponent(supportBody);
    const outlookAppHref = `ms-outlook://compose?to=${encodedTo}&subject=${encodedSubject}&body=${encodedBody}`;

    openWithFallback(outlookAppHref, outlookHref);
  };

  const openYahooCompose = () => {
    window.open(yahooHref, "_blank", "noreferrer");
  };

  const openDefaultMailCompose = () => {
    window.location.href = mailtoHref;
  };

  const handleSavePreferences = async () => {
    if (!localPrimaryNeed || !localDailyTime || !user?.uid) {
      setError("Sélectionnez une envie et une durée de référence.");
      return;
    }

const updatedProfile = {
  primaryNeed: localPrimaryNeed,
  dailyTime: localDailyTime,
  onboardingVersion: 3,
};

    setIsSaving(true);
    setError("");

    try {
      localStorage.setItem(
        `onboarding:${user.uid}`,
        JSON.stringify(updatedProfile),
      );

const userRef = doc(
  db,
  "users",
  user.uid,
);

await setDoc(
  userRef,
  {
    onboarding: {
      primaryNeed:
        localPrimaryNeed,
      dailyTime:
        localDailyTime,
      onboardingVersion: 3,
      updatedAt:
        new Date().toISOString(),
    },
  },
  { merge: true },
);

      onUpdateOnboarding?.(updatedProfile);
      setEditMode(false);
      setSuccessMessage("Votre refuge a été mis à jour.");
    } catch (saveError) {
      console.error("Erreur lors de la sauvegarde des préférences :", saveError);
      setError("Impossible d’enregistrer vos préférences pour le moment.");
    } finally {
      setIsSaving(false);
    }
  };

  const cancelPreferenceEditing = () => {
    setLocalPrimaryNeed(normalizePrimaryNeed(onboardingProfile));
    setLocalDailyTime(onboardingProfile?.dailyTime || "2min");
    setError("");
    setEditMode(false);
  };

  const handleManageSubscription = async () => {
    setError("");

    try {
      const openPortalSession = httpsCallable(functions, "createPortalSession");
      const { data } = await openPortalSession({
        returnUrl: window.location.origin + "/profil",
      });

      if (data?.url) {
        window.location.assign(data.url);
      }
    } catch (portalError) {
      console.error("Erreur lors de l’ouverture du portail Stripe :", portalError);

      const message = portalError?.message || "";

      if (
        message.includes("Aucun abonnement") ||
        message.includes("No such customer")
      ) {
        setError("Aucun abonnement n’est rattaché à ce compte. Écrivez-nous si cela vous semble inattendu.");
      } else {
        setError(
          "Impossible d’ouvrir la gestion de l’abonnement pour le moment.",
        );
      }
    }
  };

  const handleCancelSubscription = async () => {
    const isTrialing = billingInfo?.subscriptionStatus === "trialing";
    const confirmed = window.confirm(
      isTrialing
        ? "Mettre fin à votre essai ?\n\nVotre Refuge Personnel reste ouvert jusqu’à la fin des 7 jours. Aucun prélèvement ne sera effectué."
        : "Annuler votre abonnement au Refuge Personnel ?\n\nIl reste ouvert jusqu’à la fin de la période déjà réglée.",
    );

    if (!confirmed) return;

    try {
      const cancelSubscription = httpsCallable(
        functions,
        "cancelSubscription",
      );
      const { data } = await cancelSubscription();

      if (!data?.success) {
        setError("L’annulation n’a pas pu être confirmée. Votre accès reste inchangé : réessayez depuis « Abonnement et factures », ou écrivez-nous.");
        return;
      }

      const dateLabel = data.cancelAt
        ? new Date(data.cancelAt).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : null;

      setBillingInfo((current) => ({
        ...(current || {}),
        cancelAt: data.cancelAt || null,
        currentPeriodEnd: data.currentPeriodEnd || current?.currentPeriodEnd || null,
        cancelAtPeriodEnd: Boolean(data.cancelAtPeriodEnd),
        subscriptionStatus: data.subscriptionStatus || current?.subscriptionStatus || null,
      }));

      setSuccessMessage(dateLabel ? `C’est noté. Votre Refuge Personnel reste ouvert jusqu’au ${dateLabel}.` : "Votre Refuge Personnel a bien été fermé.");
    } catch (cancelError) {
      console.error("Erreur lors de l’annulation :", cancelError);
      setError("Impossible d’annuler l’abonnement. Contactez le support.");
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      "Supprimer définitivement votre compte ?\n\nToutes vos données seront effacées et cette action est irréversible.",
    );

    if (!confirmed) return;

    const confirmationText = window.prompt(
      "Pour confirmer, saisissez exactement : SUPPRIMER",
    );

    if (confirmationText !== "SUPPRIMER") {
      setError("Confirmation incorrecte. Le compte n’a pas été supprimé.");
      return;
    }

    try {
      const deleteAccount = httpsCallable(functions, "deleteUserAccount");
      await deleteAccount({ confirmText: "SUPPRIMER" });
      await signOut(auth);
      window.location.href = "/";
    } catch (deleteError) {
      console.error("Erreur lors de la suppression du compte :", deleteError);
      setError("Impossible de supprimer le compte. Contactez le support.");
    }
  };

  const handleReplayTour = async () => {
    const uid = user?.uid || "guest";
    tooltipManager.resetFullGuidedExperience(uid);

    if (user?.uid) {
      try {
        await setDoc(
          doc(db, "users", user.uid),
          { guidesSeen: { homeTour: false, playerTour: false, tooltips: [] } },
          { merge: true },
        );
      } catch (tourError) {
        console.warn("Réinitialisation de la visite guidée impossible :", tourError);
      }
    }

    setSuccessMessage("La visite guidée a été réinitialisée.");

    window.setTimeout(() => {
      window.location.href = "/";
    }, 900);
  };

  const memberSince = formatMemberSince(user.metadata);
  const planLabel = getPlanLabel(isPremium, billingInfo);
  const isSubscriptionCanceling = Boolean(billingInfo?.cancelAtPeriodEnd);
  const cancelAtRaw =
    billingInfo?.cancelAt || billingInfo?.currentPeriodEnd || null;
  const cancelAtDate =
    cancelAtRaw?.toDate?.() || (cancelAtRaw ? new Date(cancelAtRaw) : null);
  const cancelAtDisplay =
    cancelAtDate && !Number.isNaN(cancelAtDate.getTime())
      ? cancelAtDate.toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : null;

  return (
    <>
      {!embedded ? (
<TopbarInner
  title="Profil"
  onBack={() => navigate("/", { replace: true })}
  onThemeToggle={onThemeToggle}
  isPremium={isPremium}
  longestStreak={longestStreak}
  titleColor="#203e3e"
/>
      ) : null}

      <main
        className={`profile-page ${embedded ? "profile-page--embedded" : ""}`}
      >
        <header className="profile-hero">
          <h1 className="profile-sr-only">Mon refuge</h1>

          <div className="profile-avatar-wrap" ref={photoMenuRef}>
            <button
              type="button"
              className={`profile-avatar ${isPremium ? "is-premium" : ""} ${
                profilePhoto ? "has-photo" : ""
              }`}
              onClick={() => setShowPhotoMenu((current) => !current)}
              disabled={isPhotoSaving}
              aria-label="Gérer ma photo de profil"
              aria-haspopup="menu"
              aria-expanded={showPhotoMenu}
            >
              <span className="profile-avatar__media" aria-hidden="true">
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt=""
                    className="profile-avatar__image"
                  />
                ) : (
                  <span className="profile-avatar__initial">
                    {getInitial(user)}
                  </span>
                )}

                {isPhotoSaving ? (
                  <span className="profile-avatar__loading">
                    <LoaderCircle
                      className="profile-avatar__spinner"
                      size={22}
                      strokeWidth={2.2}
                    />
                  </span>
                ) : null}
              </span>
{!profilePhoto && !isPhotoSaving ? (
  <span className="profile-avatar__camera" aria-hidden="true">
    <Camera size={15} strokeWidth={2.2} />
  </span>
) : null}
            </button>

            {showPhotoMenu ? (
              <div className="profile-photo-menu" role="menu">
                <button
                  type="button"
                  className="profile-photo-menu__item"
                  role="menuitem"
                  onClick={() => {
                    setShowPhotoMenu(false);
                    photoInputRef.current?.click();
                  }}
                >
                  <Camera size={17} strokeWidth={2} aria-hidden="true" />
                  <span>
                    {profilePhoto ? "Choisir une autre photo" : "Ajouter une photo"}
                  </span>
                </button>

                {profilePhoto ? (
                  <button
                    type="button"
                    className="profile-photo-menu__item is-danger"
                    role="menuitem"
                    onClick={handleProfilePhotoDelete}
                  >
                    <Trash2 size={17} strokeWidth={2} aria-hidden="true" />
                    <span>Supprimer la photo</span>
                  </button>
                ) : null}
              </div>
            ) : null}

            <input
              ref={photoInputRef}
              className="profile-photo-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleProfilePhotoChange}
              tabIndex={-1}
              aria-hidden="true"
            />
          </div>

          <div className="profile-hero__copy">
            <p className="profile-hero__email">{user.email || "—"}</p>

            <div className="profile-hero__meta">
              <span
                className={`profile-plan ${isPremium ? "is-premium" : ""}`}
              >
                {planLabel}
              </span>

              {memberSince ? (
                <span>Membre depuis {memberSince}</span>
              ) : null}
            </div>

            {photoError ? (
              <span className="profile-hero__photo-error" role="alert">
                {photoError}
              </span>
            ) : null}
          </div>
        </header>

        <div className="profile-layout">
          <section
            className={`profile-panel profile-panel--refuge ${
              editMode ? "is-editing" : ""
            }`}
          >
            <div className="profile-panel__head">
              <div>
                <p className="profile-eyebrow">Personnalisation</p>
                <h2>Mon refuge</h2>
                <p>
                  Ces repères aident Harmonauria à vous proposer des pauses
                  plus proches de votre rythme.
                </p>
              </div>

              {!editMode ? (
                <button
                  type="button"
                  className="profile-edit-button"
                  onClick={() => {
                    setError("");
                    setEditMode(true);
                  }}
                >
                  <Pencil size={16} strokeWidth={2} aria-hidden="true" />
                  Modifier
                </button>
              ) : null}
            </div>

            {!editMode ? (
              <div className="profile-preferences-view">
                <article className="profile-preference-summary">
                  <span className="profile-preference-summary__icon">
                    <selectedNeed.Icon size={23} strokeWidth={1.9} />
                  </span>

                  <div>
                    <span className="profile-preference-summary__label">
                      De quoi avez-vous envie ?
                    </span>
                    <strong>{selectedNeed.label}</strong>
                    <p>{selectedNeed.summary}</p>
                  </div>
                </article>

                <article className="profile-preference-summary">
                  <span className="profile-preference-summary__icon">
                    <Timer size={23} strokeWidth={1.9} />
                  </span>

                  <div>
                    <span className="profile-preference-summary__label">
                      Votre durée de référence
                    </span>
                    <strong>{selectedDailyTime.label}</strong>
                    <p>
                      Cette durée sera proposée par défaut quand vous entrez
                      dans votre moment.
                    </p>
                  </div>
                </article>

              </div>
            ) : (
              <div className="profile-preferences-edit">
                <fieldset className="profile-fieldset">
                  <legend>De quoi avez-vous envie ?</legend>
                  <p>
                    Ce choix oriente délicatement les respirations et les
                    ambiances proposées dans votre refuge.
                  </p>

                  <div className="profile-choice-list">
                    {PRIMARY_NEEDS.map((option) => (
                      <PreferenceChoice
                        key={option.key}
                        option={option}
                        selected={localPrimaryNeed === option.key}
                        onClick={() => setLocalPrimaryNeed(option.key)}
                      />
                    ))}
                  </div>
                </fieldset>

                <fieldset className="profile-fieldset">
                  <legend>Quel temps souhaitez-vous vous accorder ?</legend>
                  <p>
                    Cette durée est proposée par défaut, reste modifiable pour chaque séance et sert aussi de repère à votre progression quotidienne.
                  </p>

                  <div className="profile-duration-grid">
                    {DAILY_TIMES.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`profile-duration ${
                          localDailyTime === option.value ? "is-selected" : ""
                        }`}
                        aria-pressed={localDailyTime === option.value}
                        onClick={() => setLocalDailyTime(option.value)}
                      >
                        <span>{option.label}</span>
                        <small>{option.description}</small>
                        {localDailyTime === option.value ? (
                          <Check size={17} strokeWidth={2.4} aria-hidden="true" />
                        ) : null}
                      </button>
                    ))}
                  </div>
                </fieldset>

                {error ? (
                  <div className="profile-notice is-error" role="alert">
                    <X size={18} strokeWidth={2.2} aria-hidden="true" />
                    {error}
                  </div>
                ) : null}

                <div className="profile-edit-actions">
                  <button
                    type="button"
                    className="profile-secondary-button"
                    onClick={cancelPreferenceEditing}
                    disabled={isSaving}
                  >
                    Annuler
                  </button>

                  <button
                    type="button"
                    className="profile-primary-button"
                    onClick={handleSavePreferences}
                    disabled={isSaving}
                  >
                    {isSaving ? "Enregistrement…" : "Enregistrer"}
                  </button>
                </div>
              </div>
            )}
          </section>

          {isPremium && user?.uid ? (
<section className="profile-panel profile-panel--ai">
  <div className="profile-panel__head">
    <div>
      <p className="profile-eyebrow">
        Refuge personnel
      </p>

      <h2>Mes ambiances favorites</h2>

      <p>
        Auria utilise ces trois ambiances en priorité dans vos séances,
        puis les adapte selon votre intention et vos ressentis.
      </p>
    </div>

    <button
      type="button"
      className="profile-edit-button"
      onClick={() => setFavoritesEditorOpen(true)}
    >
      <Pencil size={16} strokeWidth={2} aria-hidden="true" />
      Modifier
    </button>
  </div>

  <NatureFavoritesEditor
    uid={user.uid}
    naturalSounds={naturalSounds}
    open={favoritesEditorOpen}
    onOpenChange={setFavoritesEditorOpen}
    onSaved={() => {
      setSuccessMessage(
        "Vos ambiances favorites ont été mises à jour.",
      );
    }}
  />
</section>
          ) : null}

          {isPremium && user?.uid ? (
            <AuriaRefugesPanel
              uid={user.uid}
              onSuccess={setSuccessMessage}
            />
          ) : null}

          <section className="profile-panel profile-panel--account">
            <div className="profile-panel__head profile-panel__head--compact">
              <span className="profile-section-icon" aria-hidden="true">
                <User size={21} strokeWidth={1.9} />
              </span>

              <div>
                <p className="profile-eyebrow">Réglages</p>
                <h2>Mon compte</h2>
              </div>
            </div>

            <div className="profile-settings-list">
              {billingInfo?.stripeCustomerId ? (
                <SettingsRow
                  Icon={CreditCard}
                  title="Abonnement et factures"
                  description={
                    isSubscriptionCanceling
                      ? cancelAtDisplay
                        ? `Votre Refuge reste ouvert jusqu’au ${cancelAtDisplay}.`
                        : "Votre Refuge reste ouvert jusqu’à la fin de la période en cours."
                      : "Gérez votre formule et vos moyens de paiement."
                  }
                  actionLabel="Gérer"
                  onClick={handleManageSubscription}
                />
              ) : null}

              <SettingsRow
                Icon={Compass}
                title="Revoir la visite guidée"
                description="Redécouvrez les fonctions essentielles pas à pas."
                actionLabel="Rejouer"
                onClick={handleReplayTour}
              />

              <SettingsRow
                Icon={Mail}
                title="Besoin d’aide ?"
                description="Écrivez-nous directement depuis Harmonauria."
                actionLabel="Contacter"
                onClick={() => {
                  setSupportMessage("");
                  setShowEmailChooser(true);
                }}
              />
            </div>
          </section>

          {error && !editMode ? (
            <div className="profile-notice is-error profile-global-notice">
              <X size={18} strokeWidth={2.2} aria-hidden="true" />
              {error}
            </div>
          ) : null}

          {successMessage && !editMode ? (
            <div className="profile-notice is-success profile-global-notice">
              <Check size={18} strokeWidth={2.2} aria-hidden="true" />
              {successMessage}
            </div>
          ) : null}

          <section className="profile-session-actions">
            <button
              type="button"
              className="profile-logout-button"
              onClick={async () => {
                await onLogout?.();
                navigate("/", { replace: true });
              }}
            >
              <LogOut size={18} strokeWidth={2} aria-hidden="true" />
              Se déconnecter
            </button>

            <div className="profile-account-links">
              {billingInfo?.subscriptionId && !isSubscriptionCanceling && ["active", "trialing", "past_due", "incomplete"].includes(billingInfo?.subscriptionStatus) ? (
                <button type="button" onClick={handleCancelSubscription}>
                  Annuler mon abonnement
                </button>
              ) : null}

              <button
                type="button"
                className="is-danger"
                onClick={handleDeleteAccount}
              >
                <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
                Supprimer mon compte
              </button>
            </div>
              <nav className="profile-info-links" aria-labelledby="profile-info-title">
              <p id="profile-info-title" className="profile-info-links__title">Informations légales</p>
              <div className="profile-info-links__list">
<button type="button" onClick={() => navigate("/legal/cgv", { state: { legalOrigin: "profile", from: "/profil" } })}>Conditions de vente</button>
<button type="button" onClick={() => navigate("/legal/remboursement", { state: { legalOrigin: "profile", from: "/profil" } })}>Annulation et remboursement</button>
<button type="button" onClick={() => navigate("/legal/politique-confidentialite", { state: { legalOrigin: "profile", from: "/profil" } })}>Confidentialité</button>
<button type="button" onClick={() => navigate("/legal/mentions-legales", { state: { legalOrigin: "profile", from: "/profil" } })}>Mentions légales</button>
              </div>
            </nav>
          </section>
        </div>

      </main>

      {showEmailChooser && typeof document !== "undefined"
        ? createPortal(
            <div
              className="profile-modal-overlay"
              role="presentation"
              onClick={() => setShowEmailChooser(false)}
            >
              <section
                className="profile-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="profile-support-title"
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  className="profile-modal__close"
                  aria-label="Fermer"
                  onClick={() => setShowEmailChooser(false)}
                >
                  <X size={19} strokeWidth={2} />
                </button>

                <span className="profile-modal__icon" aria-hidden="true">
                  <Headphones size={24} strokeWidth={1.8} />
                </span>

                <h2 id="profile-support-title">
                  Comment pouvons-nous vous aider ?
                </h2>
                <p>
                  Écrivez votre message, puis choisissez la boîte mail à utiliser.
                </p>

                <textarea
                  className="profile-modal__textarea"
                  placeholder="Décrivez votre question ou votre besoin…"
                  value={supportMessage}
                  onChange={(event) => setSupportMessage(event.target.value)}
                  rows={6}
                />

                <div className="profile-modal__actions">
                  <button
                    type="button"
                    disabled={!supportMessage.trim()}
                    onClick={() => {
                      setShowEmailChooser(false);
                      openGmailCompose();
                    }}
                  >
                    Gmail
                    <ExternalLink size={15} strokeWidth={2} aria-hidden="true" />
                  </button>

                  <button
                    type="button"
                    disabled={!supportMessage.trim()}
                    onClick={() => {
                      setShowEmailChooser(false);
                      openOutlookCompose();
                    }}
                  >
                    Outlook
                    <ExternalLink size={15} strokeWidth={2} aria-hidden="true" />
                  </button>

                  <button
                    type="button"
                    disabled={!supportMessage.trim()}
                    onClick={() => {
                      setShowEmailChooser(false);
                      openYahooCompose();
                    }}
                  >
                    Yahoo
                    <ExternalLink size={15} strokeWidth={2} aria-hidden="true" />
                  </button>

                  <button
                    type="button"
                    disabled={!supportMessage.trim()}
                    onClick={() => {
                      setShowEmailChooser(false);
                      openDefaultMailCompose();
                    }}
                  >
                    Application par défaut
                  </button>
                </div>
              </section>
            </div>,
            document.body,
          )
        : null}

      <style>{`
        .profile-page,
        .profile-page * {
          box-sizing: border-box;
        }

        .profile-page {
          --profile-ink: #203e3e;
          --profile-copy: #506866;
          --profile-green: #61837a;
          --profile-sage: #76a5a7;
          --profile-pale: #cbe2db;
          --profile-line: rgba(97, 131, 122, 0.15);
          --profile-soft: rgba(203, 226, 219, 0.16);

          width: 100%;
          min-height: 100%;
          margin: 0;
          padding: 104px 20px 80px;

          color: var(--profile-ink);
          font-family: var(--font-body, "DM Sans", sans-serif);
        }

        .profile-page--embedded {
          padding: 18px 0 34px;
        }

        .profile-hero,
        .profile-layout {
          width: min(100%, 860px);
          margin-left: auto;
          margin-right: auto;
        }

.profile-hero {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 2px 28px;
  border-bottom: 0;
}

        .profile-sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        .profile-avatar-wrap {
          position: relative;
          z-index: 12;
          flex: 0 0 auto;
          width: 88px;
          height: 88px;
        }

        .profile-avatar {
          position: relative;
          width: 88px;
          height: 88px;
          margin: 0;
          padding: 0;
          border: 0;
          border-radius: 50%;
          background: transparent;
          color: #ffffff;
          font: inherit;
          cursor: pointer;
          appearance: none;
          -webkit-appearance: none;
        }

        .profile-avatar__media {
          width: 100%;
          height: 100%;
          display: grid;
          place-items: center;
          overflow: hidden;
          border: 1px solid rgba(97, 131, 122, 0.2);
          border-radius: 50%;
          background: var(--profile-ink);
          color: #ffffff;
          font-family: var(--font-display, "Nunito", sans-serif);
          font-size: 1.85rem;
          font-weight: 850;
          line-height: 1;
          box-shadow: 0 10px 24px rgba(32, 62, 62, 0.13);
          transition:
            border-color 180ms ease,
            box-shadow 180ms ease,
            transform 180ms ease;
        }

        .profile-avatar__initial {
          width: 100%;
          height: 100%;
          display: grid;
          place-items: center;
          line-height: 1;
          text-align: center;
          transform: translateY(2px);
        }

        .profile-avatar__image {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          object-position: center;
        }

.profile-avatar__camera {
  position: absolute;
  z-index: 3;
  right: -3px;
  bottom: -3px;

  width: 23px;
  height: 23px;
  padding: 0;

  display: grid;
  place-items: center;

  border: 1px solid rgba(80, 104, 102, 0.18);
  border-radius: 50%;

  background: #ffffff;
  color: #7b8f8c;

  box-shadow:
    0 5px 14px rgba(32, 62, 62, 0.14);

  pointer-events: none;

  transition:
    color 180ms ease,
    background 180ms ease,
    border-color 180ms ease,
    transform 180ms ease;
}

.profile-avatar:hover .profile-avatar__camera {
  color: #506866;
  background: #f7f9f8;
  border-color: rgba(80, 104, 102, 0.28);
  transform: scale(1.06);
}
        .profile-avatar:hover .profile-avatar__media {
          border-color: rgba(97, 131, 122, 0.46);
          box-shadow: 0 14px 30px rgba(32, 62, 62, 0.17);
          transform: translateY(-1px);
        }

        .profile-avatar:focus-visible {
          outline: 3px solid rgba(118, 165, 167, 0.35);
          outline-offset: 4px;
        }

        .profile-avatar:disabled {
          cursor: wait;
        }

        .profile-avatar:disabled .profile-avatar__media {
          opacity: 0.72;
        }

        .profile-avatar.is-premium .profile-avatar__media {
          border-color: rgba(97, 131, 122, 0.44);
          background: #4f6f68;
          color: #ffffff;
          box-shadow: 0 14px 34px rgba(97, 131, 122, 0.22);
        }

        .profile-avatar.has-photo .profile-avatar__media {
          background: #eef4f1;
        }

        .profile-avatar__loading {
          position: absolute;
          inset: 0;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: rgba(16, 24, 39, 0.38);
          color: #ffffff;
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
        }

        .profile-avatar__spinner {
          animation: profileAvatarSpin 760ms linear infinite;
        }

        .profile-photo-menu {
          position: absolute;
          z-index: 40;
          top: calc(100% + 12px);
          left: 0;
          width: max-content;
          min-width: 230px;
          padding: 7px;
          display: grid;
          gap: 3px;
          border: 1px solid rgba(97, 131, 122, 0.16);
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 18px 46px rgba(32, 62, 62, 0.16);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          animation: profilePhotoMenuIn 180ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .profile-photo-menu__item {
          width: 100%;
          min-height: 42px;
          padding: 9px 11px;
          display: flex;
          align-items: center;
          gap: 10px;
          border: 0;
          border-radius: 11px;
          background: transparent;
          color: var(--profile-ink);
          font: inherit;
          font-size: 0.84rem;
          font-weight: 750;
          text-align: left;
          cursor: pointer;
          transition: background 160ms ease, color 160ms ease;
        }

        .profile-photo-menu__item:hover,
        .profile-photo-menu__item:focus-visible {
          background: rgba(203, 226, 219, 0.24);
          outline: none;
        }

        .profile-photo-menu__item.is-danger {
          color: #a84f4a;
        }

        .profile-photo-menu__item.is-danger:hover,
        .profile-photo-menu__item.is-danger:focus-visible {
          background: rgba(192, 72, 66, 0.08);
          color: #b42318;
        }

        .profile-photo-input {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        .profile-hero__photo-error {
          display: block;
          margin-top: 7px;
          color: #b42318;
          font-size: 0.74rem;
          font-weight: 700;
          line-height: 1.4;
        }

        @keyframes profilePhotoMenuIn {
          from {
            opacity: 0;
            transform: translateY(-5px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes profileAvatarSpin {
          to {
            transform: rotate(360deg);
          }
        }

        .profile-hero__copy {
          min-width: 0;
          flex: 1;
        }

        .profile-eyebrow {
          margin: 0 0 7px;
          color: var(--profile-green);
          font-size: 0.69rem;
          font-weight: 850;
          line-height: 1;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .profile-hero__email {
          max-width: min(74vw, 620px);
          margin: 0;
          overflow: hidden;
          color: var(--profile-ink);
          font-size: clamp(0.98rem, 2vw, 1.08rem);
          font-weight: 800;
          letter-spacing: -0.015em;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .profile-hero__meta {
          margin-top: 8px;
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 9px 12px;
          color: rgba(80, 104, 102, 0.7);
          font-size: 0.78rem;
          font-weight: 650;
        }

        .profile-plan {
          display: inline-flex;
          align-items: center;
          min-height: 27px;
          padding: 4px 11px;
          border: 1px solid var(--profile-line);
          border-radius: 999px;
          background: var(--profile-soft);
          color: var(--profile-green);
          font-weight: 800;
        }

        .profile-plan.is-premium {
          border-color: rgba(97, 131, 122, 0.34);
          background: rgba(203, 226, 219, 0.34);
          color: #4f6f68;
        }

        .profile-layout {
          display: grid;
          gap: 18px;
        }

        .profile-panel {
          width: 100%;
          padding: clamp(22px, 4vw, 32px);
          border: 1px solid var(--profile-line);
          border-radius: 26px;
          background: rgba(255, 255, 255, 0.78);
          box-shadow: 0 14px 42px rgba(49, 72, 66, 0.055);
        }

        .profile-panel__head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 24px;
        }

        .profile-panel__head--compact {
          justify-content: flex-start;
          align-items: center;
          margin-bottom: 18px;
        }

        .profile-panel__head h2 {
          margin: 0;
          color: var(--profile-ink);
          font-family: var(--font-display, "Nunito", sans-serif);
          font-size: clamp(1.35rem, 3vw, 1.72rem);
          font-weight: 800;
          line-height: 1.14;
          letter-spacing: -0.025em;
        }

        .profile-panel__head > div > p:last-child {
          max-width: 590px;
          margin: 8px 0 0;
          color: var(--profile-copy);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .profile-section-icon {
          flex: 0 0 auto;
          width: 44px;
          height: 44px;
          display: grid;
          place-items: center;
          border-radius: 15px;
          background: var(--profile-soft);
          color: var(--profile-green);
        }

        .profile-edit-button {
          flex: 0 0 auto;
          min-height: 42px;
          padding: 0 15px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: 1px solid var(--profile-line);
          border-radius: 999px;
          background: #ffffff;
          color: var(--profile-green);
          font: inherit;
          font-size: 0.82rem;
          font-weight: 800;
          cursor: pointer;
          transition:
            transform 180ms ease,
            border-color 180ms ease,
            background 180ms ease;
        }

        .profile-edit-button:hover {
          transform: translateY(-1px);
          border-color: rgba(97, 131, 122, 0.34);
          background: rgba(203, 226, 219, 0.13);
        }

        .profile-preferences-view {
          display: grid;
          gap: 12px;
        }

        .profile-preference-summary {
          min-height: 112px;
          padding: 18px;
          display: grid;
          grid-template-columns: 48px minmax(0, 1fr);
          align-items: start;
          gap: 15px;
          border: 1px solid rgba(97, 131, 122, 0.11);
          border-radius: 20px;
          background: rgba(247, 251, 250, 0.58);
        }

        .profile-preference-summary__icon {
          width: 48px;
          height: 48px;
          display: grid;
          place-items: center;
          border-radius: 16px;
          background: rgba(203, 226, 219, 0.28);
          color: var(--profile-green);
        }

        .profile-preference-summary__label {
          display: block;
          margin-bottom: 5px;
          color: rgba(80, 104, 102, 0.68);
          font-size: 0.73rem;
          font-weight: 800;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .profile-preference-summary strong {
          display: block;
          color: var(--profile-ink);
          font-family: var(--font-display, "Nunito", sans-serif);
          font-size: 1.08rem;
          font-weight: 800;
        }

        .profile-preference-summary p {
          margin: 5px 0 0;
          color: var(--profile-copy);
          font-size: 0.87rem;
          line-height: 1.55;
        }

        .profile-fieldset {
          min-width: 0;
          margin: 0;
          padding: 0;
          border: 0;
        }

        .profile-fieldset + .profile-fieldset {
          margin-top: 30px;
          padding-top: 30px;
          border-top: 1px solid var(--profile-line);
        }

        .profile-fieldset legend {
          padding: 0;
          color: var(--profile-ink);
          font-family: var(--font-display, "Nunito", sans-serif);
          font-size: 1.08rem;
          font-weight: 800;
        }

        .profile-fieldset > p {
          margin: 7px 0 17px;
          color: var(--profile-copy);
          font-size: 0.86rem;
          line-height: 1.55;
        }

        .profile-choice-list {
          display: grid;
          gap: 10px;
        }

        .profile-choice {
          width: 100%;
          min-height: 78px;
          padding: 13px 15px;
          display: grid;
          grid-template-columns: 46px minmax(0, 1fr) 28px;
          align-items: center;
          gap: 13px;
          border: 1px solid var(--profile-line);
          border-radius: 19px;
          background: #ffffff;
          color: var(--profile-ink);
          font: inherit;
          text-align: left;
          cursor: pointer;
          transition:
            transform 180ms ease,
            border-color 180ms ease,
            background 180ms ease;
        }

        .profile-choice:hover {
          transform: translateY(-1px);
          border-color: rgba(97, 131, 122, 0.34);
        }

        .profile-choice.is-selected {
          border-color: rgba(97, 131, 122, 0.42);
          background: rgba(203, 226, 219, 0.16);
        }

        .profile-choice__icon {
          width: 46px;
          height: 46px;
          display: grid;
          place-items: center;
          border-radius: 15px;
          background: var(--profile-soft);
          color: var(--profile-green);
        }

        .profile-choice__copy {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .profile-choice__copy strong {
          font-size: 0.94rem;
          font-weight: 800;
        }

        .profile-choice__copy span {
          color: var(--profile-copy);
          font-size: 0.8rem;
          line-height: 1.45;
        }

        .profile-choice__check {
          width: 28px;
          height: 28px;
          display: grid;
          place-items: center;
          border: 1px solid var(--profile-line);
          border-radius: 50%;
          color: var(--profile-green);
        }

        .profile-choice.is-selected .profile-choice__check {
          border-color: var(--profile-green);
          background: var(--profile-green);
          color: #ffffff;
        }

        .profile-duration-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }

        .profile-duration {
          position: relative;
          min-height: 94px;
          padding: 15px 36px 15px 15px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          gap: 4px;
          border: 1px solid var(--profile-line);
          border-radius: 19px;
          background: #ffffff;
          color: var(--profile-ink);
          font: inherit;
          text-align: left;
          cursor: pointer;
          transition:
            transform 180ms ease,
            border-color 180ms ease,
            background 180ms ease;
        }

        .profile-duration:hover {
          transform: translateY(-1px);
          border-color: rgba(97, 131, 122, 0.34);
        }

        .profile-duration.is-selected {
          border-color: rgba(97, 131, 122, 0.42);
          background: rgba(203, 226, 219, 0.16);
        }

        .profile-duration > span {
          font-size: 0.98rem;
          font-weight: 800;
        }

        .profile-duration small {
          color: var(--profile-copy);
          font-size: 0.76rem;
          line-height: 1.4;
        }

        .profile-duration svg {
          position: absolute;
          top: 14px;
          right: 14px;
          color: var(--profile-green);
        }

        .profile-edit-actions {
          margin-top: 28px;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        .profile-primary-button,
        .profile-secondary-button {
          min-height: 48px;
          padding: 0 20px;
          border-radius: 999px;
          font: inherit;
          font-size: 0.88rem;
          font-weight: 800;
          cursor: pointer;
          transition:
            transform 180ms ease,
            opacity 180ms ease,
            background 180ms ease;
        }

        .profile-primary-button {
          border: 1px solid var(--profile-green);
          background: var(--profile-green);
          color: #ffffff;
          box-shadow: 0 12px 28px rgba(97, 131, 122, 0.18);
        }

        .profile-secondary-button {
          border: 1px solid var(--profile-line);
          background: #ffffff;
          color: var(--profile-copy);
        }

        .profile-primary-button:hover,
        .profile-secondary-button:hover {
          transform: translateY(-1px);
        }

        .profile-primary-button:disabled,
        .profile-secondary-button:disabled {
          opacity: 0.55;
          cursor: wait;
        }

        .profile-refuges__list {
          margin: 0;
          padding: 0;
          display: grid;
          gap: 10px;
          list-style: none;
        }

        .profile-refuges__item {
          min-height: 76px;
          padding: 14px 15px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          border: 1px solid rgba(97, 131, 122, 0.11);
          border-radius: 19px;
          background: rgba(247, 251, 250, 0.58);
        }

        .profile-refuges__copy {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .profile-refuges__copy strong {
          color: var(--profile-ink);
          font-family: var(--font-display, "Nunito", sans-serif);
          font-size: 0.98rem;
          font-weight: 800;
          line-height: 1.25;
        }

        .profile-refuges__detail {
          color: var(--profile-copy);
          font-size: 0.8rem;
          line-height: 1.45;
          overflow-wrap: anywhere;
        }

        .profile-refuges__date {
          color: rgba(80, 104, 102, 0.62);
          font-size: 0.72rem;
          font-weight: 650;
        }

        .profile-refuges__actions {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .profile-refuges__play {
          min-height: 42px;
          padding: 0 16px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: 1px solid var(--profile-green);
          border-radius: 999px;
          background: var(--profile-green);
          color: #ffffff;
          font: inherit;
          font-size: 0.82rem;
          font-weight: 800;
          cursor: pointer;
          transition: transform 180ms ease, filter 180ms ease;
        }

        .profile-refuges__play:hover {
          transform: translateY(-1px);
          filter: brightness(1.06);
        }

        .profile-refuges__forget {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border: 1px solid var(--profile-line);
          border-radius: 50%;
          background: #ffffff;
          color: rgba(80, 104, 102, 0.62);
          cursor: pointer;
          transition: color 180ms ease, border-color 180ms ease;
        }

        .profile-refuges__forget:hover:not(:disabled) {
          border-color: rgba(165, 29, 29, 0.24);
          color: rgba(165, 29, 29, 0.78);
        }

        .profile-refuges__forget:disabled {
          opacity: 0.45;
          cursor: wait;
        }

        .profile-refuges__empty {
          margin: 0;
          color: var(--profile-copy);
          font-size: 0.87rem;
          line-height: 1.55;
        }

        .profile-refuges__hidden {
          margin-top: 18px;
          padding-top: 17px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 11px;
          border-top: 1px solid var(--profile-line);
        }

        .profile-refuges__hidden p {
          margin: 0;
          color: var(--profile-copy);
          font-size: 0.82rem;
          line-height: 1.5;
        }

        .profile-refuges__restore {
          min-height: 40px;
          padding: 0 15px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: 1px solid var(--profile-line);
          border-radius: 999px;
          background: var(--profile-soft);
          color: var(--profile-green);
          font: inherit;
          font-size: 0.8rem;
          font-weight: 800;
          cursor: pointer;
          transition: transform 180ms ease, border-color 180ms ease;
        }

        .profile-refuges__restore:hover:not(:disabled) {
          transform: translateY(-1px);
          border-color: rgba(97, 131, 122, 0.34);
        }

        .profile-refuges__restore:disabled {
          opacity: 0.55;
          cursor: wait;
        }

        .profile-refuges__error {
          margin-top: 15px;
        }

        .profile-refuges__play:focus-visible,
        .profile-refuges__forget:focus-visible,
        .profile-refuges__restore:focus-visible {
          outline: 3px solid rgba(118, 165, 167, 0.35);
          outline-offset: 3px;
        }

        .profile-settings-list {
          border-top: 1px solid var(--profile-line);
        }

        .profile-settings-row {
          width: 100%;
          min-height: 78px;
          padding: 15px 2px;
          display: grid;
          grid-template-columns: 42px minmax(0, 1fr) auto;
          align-items: center;
          gap: 13px;
          border: 0;
          background: transparent;
          color: var(--profile-ink);
          font: inherit;
          text-align: left;
          cursor: pointer;
        }

        .profile-settings-row:last-child {
          border-bottom: 0;
        }

        .profile-settings-row__icon {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border-radius: 14px;
          background: var(--profile-soft);
          color: var(--profile-green);
        }

        .profile-settings-row__copy {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .profile-settings-row__copy strong {
          font-size: 0.91rem;
          font-weight: 800;
        }

        .profile-settings-row__copy span {
          color: var(--profile-copy);
          font-size: 0.78rem;
          line-height: 1.45;
        }

        .profile-settings-row__action {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          color: var(--profile-green);
          font-size: 0.76rem;
          font-weight: 800;
        }

        .profile-settings-row:hover .profile-settings-row__action {
          transform: translateX(2px);
        }

        .profile-notice {
          padding: 12px 14px;
          display: flex;
          align-items: flex-start;
          gap: 9px;
          border-radius: 14px;
          font-size: 0.83rem;
          font-weight: 700;
          line-height: 1.45;
        }

        .profile-notice svg {
          flex: 0 0 auto;
          margin-top: 1px;
        }

        .profile-notice.is-success {
          border: 1px solid rgba(97, 131, 122, 0.18);
          background: rgba(203, 226, 219, 0.2);
          color: var(--profile-green);
        }

        .profile-notice.is-error {
          border: 1px solid rgba(185, 28, 28, 0.15);
          background: rgba(254, 242, 242, 0.78);
          color: #a51d1d;
        }

        .profile-global-notice {
          width: 100%;
        }

        .profile-session-actions {
          padding: 12px 2px 0;
        }

        .profile-logout-button {
          width: 100%;
          min-height: 52px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          border: 1px solid var(--profile-line);
          border-radius: 999px;
          background: #ffffff;
          color: var(--profile-copy);
          font: inherit;
          font-size: 0.88rem;
          font-weight: 800;
          cursor: pointer;
          transition:
            border-color 180ms ease,
            color 180ms ease,
            transform 180ms ease;
        }

        .profile-logout-button:hover {
          transform: translateY(-1px);
          border-color: rgba(97, 131, 122, 0.32);
          color: var(--profile-ink);
        }

        .profile-account-links {
          margin-top: 16px;
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 8px 18px;
        }

        .profile-account-links button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 0;
          background: transparent;
          color: rgba(80, 104, 102, 0.7);
          font: inherit;
          font-size: 0.73rem;
          font-weight: 700;
          cursor: pointer;
        }

        .profile-account-links button:hover {
          color: var(--profile-ink);
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .profile-account-links button.is-danger {
          color: rgba(165, 29, 29, 0.72);
        }

                .profile-info-links {
          margin-top: 26px;
          padding-top: 18px;
          border-top: 1px solid var(--profile-line);
          text-align: center;
        }

        .profile-info-links__title {
          margin: 0 0 10px;
          color: rgba(80, 104, 102, 0.62);
          font-size: 0.66rem;
          font-weight: 850;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .profile-info-links__list {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 4px 16px;
        }

        .profile-info-links__list button {
          min-height: 32px;
          padding: 0 2px;
          border: 0;
          background: transparent;
          color: rgba(80, 104, 102, 0.72);
          font: inherit;
          font-size: 0.74rem;
          font-weight: 700;
          cursor: pointer;
        }

        .profile-info-links__list button:hover {
          color: var(--profile-ink);
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .profile-info-links__list button:focus-visible {
          outline: 2px solid rgba(118, 165, 167, 0.5);
          outline-offset: 2px;
          border-radius: 6px;
        }
          
        .profile-modal-overlay,
        .profile-modal-overlay * {
          box-sizing: border-box;
        }

        .profile-modal-overlay {
          --profile-ink: #203e3e;
          --profile-copy: #506866;
          --profile-green: #61837a;
          --profile-line: rgba(97, 131, 122, 0.15);
          --profile-soft: rgba(203, 226, 219, 0.16);

          position: fixed;
          inset: 0;
          z-index: 10080;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
          padding:
            max(24px, calc(env(safe-area-inset-top, 0px) + 16px))
            16px
            max(24px, calc(env(safe-area-inset-bottom, 0px) + 16px));
          display: flex;
          align-items: flex-start;
          justify-content: center;
          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          background: rgba(21, 35, 37, 0.32);
          backdrop-filter: blur(11px);
          -webkit-backdrop-filter: blur(11px);
          color: var(--profile-ink);
          font-family: var(--font-body, "DM Sans", sans-serif);
          animation: profileOverlayIn 180ms ease both;
        }

        .profile-modal {
          position: relative;
          width: min(100%, 520px);
          max-height: calc(
            100dvh - env(safe-area-inset-top, 0px) -
              env(safe-area-inset-bottom, 0px) - 48px
          );
          margin: auto 0;
          padding: clamp(23px, 5vw, 32px);
          overflow-y: auto;
          border: 1px solid var(--profile-line);
          border-radius: 26px;
          background: #ffffff;
          color: var(--profile-ink);
          box-shadow: 0 26px 80px rgba(21, 35, 37, 0.18);
          text-align: center;
          animation: profileModalIn 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .profile-modal__close {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 38px;
          height: 38px;
          display: grid;
          place-items: center;
          border: 0;
          border-radius: 50%;
          background: var(--profile-soft);
          color: var(--profile-copy);
          cursor: pointer;
        }

        .profile-modal__icon {
          width: 54px;
          height: 54px;
          margin: 0 auto 18px;
          display: grid;
          place-items: center;
          border-radius: 18px;
          background: var(--profile-soft);
          color: var(--profile-green);
        }

        .profile-modal h2 {
          margin: 0;
          font-family: var(--font-display, "Nunito", sans-serif);
          font-size: 1.55rem;
          font-weight: 800;
          letter-spacing: -0.03em;
        }

        .profile-modal > p {
          max-width: 390px;
          margin: 9px auto 0;
          color: var(--profile-copy);
          font-size: 0.88rem;
          line-height: 1.6;
        }

        .profile-modal__textarea {
          width: 100%;
          min-height: 128px;
          margin-top: 20px;
          padding: 14px 15px;
          resize: vertical;
          border: 1px solid var(--profile-line);
          border-radius: 17px;
          background: rgba(247, 251, 250, 0.52);
          color: var(--profile-ink);
          font: inherit;
          font-size: 16px;
          line-height: 1.55;
          outline: none;
        }

        .profile-modal__textarea:focus {
          border-color: rgba(97, 131, 122, 0.42);
          box-shadow: 0 0 0 4px rgba(203, 226, 219, 0.23);
        }

        .profile-modal__actions {
          margin-top: 13px;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 9px;
        }

        .profile-modal__actions button {
          min-height: 46px;
          padding: 0 13px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          border: 1px solid var(--profile-line);
          border-radius: 14px;
          background: #ffffff;
          color: var(--profile-ink);
          font: inherit;
          font-size: 0.82rem;
          font-weight: 800;
          cursor: pointer;
        }

        .profile-modal__actions button:hover:not(:disabled) {
          border-color: rgba(97, 131, 122, 0.35);
          background: var(--profile-soft);
        }

        .profile-modal__actions button:disabled {
          opacity: 0.42;
          cursor: not-allowed;
        }

        .profile-empty-state {
          min-height: 100dvh;
          display: grid;
          place-content: center;
          text-align: center;
          color: #203e3e;
        }

        @keyframes profileOverlayIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes profileModalIn {
          from {
            opacity: 0;
            transform: translateY(14px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @media (max-width: 700px) {
          .profile-page {
            padding: 88px 12px 64px;
          }

          .profile-page--embedded {
            padding: 10px 0 26px;
          }

          .profile-hero {
            gap: 13px;
            padding: 4px 2px 22px;
          }

          .profile-avatar-wrap,
          .profile-avatar {
            width: 76px;
            height: 76px;
          }

          .profile-avatar__media {
            font-size: 1.55rem;
          }

          .profile-photo-menu {
            min-width: 218px;
          }

          .profile-hero__email {
            max-width: calc(100vw - 126px);
            font-size: 0.95rem;
          }

          .profile-hero__meta {
            gap: 7px 10px;
            font-size: 0.72rem;
          }

          .profile-panel {
            padding: 20px 14px;
            border-radius: 22px;
          }

          .profile-panel__head {
            flex-direction: column;
            align-items: stretch;
            margin-bottom: 20px;
          }

          .profile-panel__head--compact {
            flex-direction: row;
            align-items: center;
          }

          .profile-edit-button {
            width: 100%;
          }

          .profile-preference-summary {
            min-height: 0;
            padding: 15px;
            grid-template-columns: 43px minmax(0, 1fr);
            gap: 12px;
          }

          .profile-preference-summary__icon {
            width: 43px;
            height: 43px;
            border-radius: 14px;
          }

          .profile-duration-grid {
            grid-template-columns: 1fr;
          }

          .profile-duration {
            min-height: 80px;
          }

          .profile-edit-actions {
            flex-direction: column-reverse;
          }

          .profile-primary-button,
          .profile-secondary-button {
            width: 100%;
          }

          .profile-refuges__item {
            align-items: stretch;
            flex-direction: column;
            padding: 15px;
          }

          .profile-refuges__actions {
            width: 100%;
            justify-content: space-between;
          }

          .profile-refuges__play {
            flex: 1;
            justify-content: center;
          }

          .profile-refuges__hidden {
            flex-direction: column;
            align-items: stretch;
          }

          .profile-refuges__restore {
            justify-content: center;
          }

          .profile-settings-row {
            grid-template-columns: 40px minmax(0, 1fr) 22px;
            padding: 14px 0;
          }

          .profile-settings-row__action > span {
            display: none;
          }

          .profile-modal__actions {
            grid-template-columns: 1fr;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .profile-avatar__spinner,
          .profile-photo-menu {
            animation: none !important;
          }

          .profile-modal-overlay,
          .profile-modal {
            animation: none !important;
          }

          .profile-page *,
          .profile-page *::before,
          .profile-page *::after {
            scroll-behavior: auto !important;
            transition-duration: 0.01ms !important;
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </>
  );
}
